import { useCallback, useEffect, useRef } from 'react'
import { Analytics, AnalyticsEvent } from '../analytics/Analytics'
import { usePotentials } from '../basket/potentials'
import { isOverCap } from '../basket/voucherCap'
import {
  ConsumerPotentialsDocument,
  EnumTradeInPayoutType,
  useConsumerCarriersQuery,
  useTradeInCreateMutation,
} from '../generated/graphql'
import { useTranslation } from '../localization/useTranslation'
import { useAuthStore } from '../store/auth'
import { useCheckoutStore } from '../store/checkout'
import { useSetPageTitle } from '../store/pageTitle'
import { useToast } from '../ui/toast'

/**
 * Shipping step: the carrier the customer uses to send their parcel to our warehouse. The options
 * come from Core (`ConsumerCarriers`, address/country aware — same source as v1's DeliveryPage);
 * the chosen carrier's `_id` is stored as `carrierId`. We default-select the first option (v1 does
 * the same). Pressing "Confirm trade-in" runs `onBeforeNext` here: it fires `TradeInCreate` (carrier
 * fallback + error handling, ported from DeliveryPage), snapshots the basket for the Confirm step,
 * emits v1's completion event, and only advances on success.
 */
export function ShippingPage() {
  const { potentials, reward } = usePotentials()
  const shopId = useAuthStore((s) => s.shopId)
  const consumerId = useAuthStore((s) => s.consumerId)
  const maximumVoucher = useAuthStore((s) => s.config.maximumVoucher) ?? 0
  const carrierId = useCheckoutStore((s) => s.carrierId)
  const setCarrierId = useCheckoutStore((s) => s.setCarrierId)
  const setOnBeforeNext = useCheckoutStore((s) => s.setOnBeforeNext)
  const showToast = useToast((s) => s.show)
  const { t } = useTranslation()
  const n = potentials.length
  useSetPageTitle(
    t('shipping.title'),
    n === 1 ? t('shipping.subtitle', { count: n }) : t('shipping.subtitlePlural', { count: n }),
  )

  const { data, loading } = useConsumerCarriersQuery({
    variables: { shopId: shopId ?? '', consumerId: consumerId ?? '' },
    skip: !shopId || !consumerId,
  })
  const carriers = (data?.ConsumerCarriers ?? []).flatMap((c) => (c ? [c] : []))

  // Default-select the first carrier once options resolve (and if the current pick vanished).
  useEffect(() => {
    if (carriers.length === 0) return
    if (!carrierId || !carriers.some((c) => c._id === carrierId)) {
      setCarrierId(carriers[0]._id)
    }
  }, [carriers, carrierId, setCarrierId])

  // Latest values for the submit handler (registered once, reads fresh data via the ref).
  const submitRef = useRef({
    consumerId,
    potentialIds: [] as string[],
    reward,
    count: n,
    maximumVoucher,
    fallbackCarrier: '' as string | undefined,
    carrierName: 'Unknown',
  })
  submitRef.current = {
    consumerId,
    potentialIds: potentials.map((p) => p._id),
    reward,
    count: n,
    maximumVoucher,
    fallbackCarrier: carriers[0]?._id,
    carrierName:
      carriers.find((carrier) => carrier._id === carrierId)?.shippingCarrier?.name ??
      carriers[0]?.shippingCarrier?.name ??
      'Unknown',
  }

  const [createTradeIn] = useTradeInCreateMutation({
    errorPolicy: 'all',
    refetchQueries: [{ query: ConsumerPotentialsDocument }],
    awaitRefetchQueries: true,
  })

  // Create the trade-in from the basket, ported from v1's DeliveryPage.onNextPath.
  const submit = useCallback(async (): Promise<boolean> => {
    const {
      consumerId: cid,
      potentialIds,
      reward: rw,
      count,
      maximumVoucher: max,
      fallbackCarrier,
      carrierName,
    } = submitRef.current
    if (!cid || potentialIds.length === 0) return false
    if (isOverCap(rw, max)) {
      showToast(t('shipping.maxExceeded'))
      return false
    }
    const store = useCheckoutStore.getState()
    const comments = Object.entries(store.stories)
      .filter(([, txt]) => txt.trim().length > 0)
      .map(([potentialId, comment]) => ({ potentialId, comment: comment.trim() }))
    const failed = (message: string) => {
      const maxed = message.includes('Exceeded the maximum voucher')
      showToast(maxed ? t('shipping.maxExceeded') : t('shipping.createFailed'))
      return false
    }
    try {
      const res = await createTradeIn({
        variables: {
          consumerId: cid,
          potentialIds,
          brandCarrierId: store.carrierId ?? fallbackCarrier,
          payout: EnumTradeInPayoutType.Voucher,
          comments,
        },
      })
      const id = res.data?.TradeInCreate?._id
      if ((res.errors ?? []).length > 0 || !id) {
        return failed(res.errors?.[0]?.message ?? '')
      }
      store.setTradeInResult(id, rw, count)
      Analytics.event(AnalyticsEvent.trackCompletion, { tradeInCarrier: carrierName })
      return true
    } catch (e) {
      return failed(e instanceof Error ? e.message : '')
    }
  }, [createTradeIn, showToast, t])

  useEffect(() => {
    setOnBeforeNext(submit)
    return () => setOnBeforeNext(null)
  }, [submit, setOnBeforeNext])

  if (loading) {
    return (
      <div className="center-note">
        <p>{t('shipping.loading')}</p>
      </div>
    )
  }

  if (carriers.length === 0) {
    return (
      <div className="center-note">
        <p>{t('shipping.none')}</p>
      </div>
    )
  }

  return (
    <div className="step">
      <div className="ship-list">
        {carriers.map((c) => {
          const key = c.shippingCarrier?.localizationKey || c.shippingCarrier?.carrierId || ''
          // Prefer the carrier's customer-facing externalName; fall back to name, then the key.
          const name = c.shippingCarrier?.externalName || c.shippingCarrier?.name || key
          const logo = c.shippingCarrier?.logo
          // Description is optional flavour; unknown carriers fall back to no description.
          const descKey = `shipping.desc_${key}`
          const desc = t(descKey)
          const hasDesc = desc !== descKey
          const sel = carrierId === c._id
          return (
            <button
              key={c._id}
              type="button"
              className={`ship ${sel ? 'sel' : ''}`}
              aria-pressed={sel}
              onClick={() => setCarrierId(c._id)}
            >
              <span className="logo">
                {logo ? <img src={logo} alt="" /> : (name || '?').slice(0, 3).toUpperCase()}
              </span>
              <span className="meta">
                <div className="nm">{name}</div>
                {hasDesc && <div className="desc">{desc}</div>}
              </span>
              <span className="radio" />
            </button>
          )
        })}
      </div>
    </div>
  )
}
